import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { showToast } from '../../store/uiSlice';
import { adminApi } from '../../services/api';

const AffiliateList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [affiliates, setAffiliates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', source_name: '' });

  useEffect(() => { fetchAffiliates(); }, []);

  const fetchAffiliates = async () => {
    try {
      const res = await adminApi.get('/leads/affiliates/list');
      setAffiliates(res.data?.affiliates || []);
    } catch (e) {}
    finally { setIsLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.name || !form.source_name) { dispatch(showToast({ message: 'Name and source name required', type: 'error' })); return; }
    try {
      await adminApi.post('/leads/affiliates/create', form);
      dispatch(showToast({ message: 'Affiliate created', type: 'success' }));
      setForm({ name: '', source_name: '' });
      setShowForm(false);
      fetchAffiliates();
    } catch (e) { dispatch(showToast({ message: e.response?.data?.message || 'Failed', type: 'error' })); }
  };

  const handleToggle = async (id, currentActive) => {
    try {
      await adminApi.put(`/leads/affiliates/${id}`, { is_active: !currentActive });
      dispatch(showToast({ message: `Affiliate ${currentActive ? 'deactivated' : 'activated'}`, type: 'success' }));
      fetchAffiliates();
    } catch (e) { dispatch(showToast({ message: 'Failed', type: 'error' })); }
  };

  const handleDelete = async (id, name) => {
    if (!confirm(`Delete affiliate "${name}"?`)) return;
    try { await adminApi.delete(`/leads/affiliates/${id}`); dispatch(showToast({ message: 'Affiliate deleted', type: 'success' })); fetchAffiliates(); }
    catch (e) { dispatch(showToast({ message: 'Failed', type: 'error' })); }
  };

  if (isLoading) return (
    <div className="flex justify-center py-20">
      <div className="w-10 h-10 border-[3px] border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-5 border-b-2 border-[#e2e8f0] bg-[#f8fafc]">
        <div>
          <h2 className="font-syne text-xl font-extrabold text-[#0b1e33] uppercase tracking-tight">AFFILIATE MARKETERS</h2>
          <p className="text-[11px] text-[#475569] font-mono font-extrabold mt-1">
            <span className="text-[#1e3a5f]">{affiliates.length}</span> AFFILIATES · MANAGE API KEYS AND FUNNELS
          </p>
        </div>
        <button onClick={() => setShowForm(!showForm)} 
          className="px-5 py-2.5 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[3px] border-[#0b1e33]">
          <i className="fas fa-plus mr-1.5" /> ADD AFFILIATE
        </button>
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="mx-5 mt-4 p-5 bg-white border-2 border-[#1e3a5f] rounded-xl shadow-lg">
          <div className="flex gap-3 mb-4">
            <input 
              type="text" 
              value={form.name} 
              onChange={e => setForm({ ...form, name: e.target.value })} 
              placeholder="Affiliate Name" 
              className="flex-1 bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-xs font-extrabold outline-none focus:border-[#1e3a5f] transition-all placeholder:text-[#94a3b8]" 
            />
            <input 
              type="text" 
              value={form.source_name} 
              onChange={e => setForm({ ...form, source_name: e.target.value })} 
              placeholder="Source/Funnel Name" 
              className="flex-1 bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-xs font-extrabold outline-none focus:border-[#1e3a5f] transition-all placeholder:text-[#94a3b8]" 
            />
          </div>
          <button onClick={handleCreate} 
            className="px-6 py-2.5 rounded-xl bg-[#059669] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#047857] transition-all border-b-[3px] border-[#047857]">
            <i className="fas fa-check mr-1.5" /> CREATE AFFILIATE
          </button>
        </div>
      )}

      {/* Empty State */}
      {affiliates.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-20 h-20 rounded-2xl bg-[#f1f5f9] flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-users-gear text-[#94a3b8] text-3xl" />
          </div>
          <p className="text-[#475569] font-syne font-extrabold text-base uppercase tracking-wider mb-1">NO AFFILIATES YET</p>
          <p className="text-[#94a3b8] text-[11px] font-bold">Create your first affiliate marketer</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#f1f5f9] border-b-2 border-[#e2e8f0] text-[9px] text-[#475569] font-mono font-extrabold uppercase tracking-wider">
                <th className="text-left px-5 py-3.5">NAME</th>
                <th className="text-left px-5 py-3.5">SOURCE/FUNNEL</th>
                <th className="text-left px-5 py-3.5">API KEY</th>
                <th className="text-center px-5 py-3.5">LEADS</th>
                <th className="text-center px-5 py-3.5">STATUS</th>
                <th className="text-center px-5 py-3.5">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {affiliates.map(aff => (
                <tr key={aff.id} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-all">
                  {/* Name */}
                  <td className="px-5 py-3.5 font-syne font-extrabold text-[#0f172a] text-xs">{aff.name}</td>
                  {/* Source */}
                  <td className="px-5 py-3.5 text-[#1e3a5f] text-[12px] font-mono font-extrabold">{aff.source_name}</td>
                  {/* API Key */}
                  <td className="px-5 py-3.5">
                    <code className="text-[#1e3a5f] text-[11px] font-mono font-extrabold bg-[#eff6ff] border border-[#bfdbfe] px-3 py-1 rounded-lg">{aff.api_key?.slice(0, 20)}...</code>
                  </td>
                  {/* Leads */}
                  <td className="px-5 py-3.5 text-center">
                    <span className="inline-flex items-center justify-center min-w-[40px] px-3 py-1 rounded-lg bg-[#1e3a5f] text-white text-[12px] font-mono font-extrabold">
                      {aff.lead_count || 0}
                    </span>
                  </td>
                  {/* Status */}
                  <td className="px-5 py-3.5 text-center">
                    <button 
                      onClick={() => handleToggle(aff.id, aff.is_active)} 
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-mono font-extrabold uppercase tracking-wider transition-all border-2 ${
                        aff.is_active 
                          ? 'bg-[#059669] text-white border-[#047857] hover:bg-[#dc2626] hover:border-[#b91c1c]' 
                          : 'bg-[#dc2626] text-white border-[#b91c1c] hover:bg-[#059669] hover:border-[#047857]'
                      }`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-white" />
                      {aff.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </button>
                  </td>
                  {/* Actions */}
                  <td className="px-5 py-3.5 text-center">
                    <div className="flex gap-2 justify-center">
                      <button 
                        onClick={() => navigate(`/admin/affiliate/${aff.id}`)} 
                        className="px-3 py-1.5 rounded-lg bg-[#1e3a5f] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]">
                        <i className="fas fa-eye mr-1" /> VIEW
                      </button>
                      <button 
                        onClick={() => handleDelete(aff.id, aff.name)} 
                        className="px-3 py-1.5 rounded-lg bg-[#dc2626] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#b91c1c] transition-all border-b-[2px] border-[#b91c1c]">
                        <i className="fas fa-trash-alt mr-1" /> DELETE
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AffiliateList;